/**
 * Recap モーダル表示用フォーマッタ純関数群。
 *
 * Decimal 入力:
 *   proto google.type.Decimal の value フィールドを string として受け取る
 *   (= decimalString() の戻り値、 または Decimal?.value)。
 *   未設定 (undefined / 空文字) の場合は「—」 を返す。
 */
import { formatPartnerWithCode } from '~/pages/SupplyBilateral/models/formatters'

type CivilDate = { year: number; month: number; day: number }

/** 代替表示文字 (= null / 未設定値の placeholder)。 */
const FALLBACK = '—'

function padTwo(n: number): string {
  return n.toString().padStart(2, '0')
}

function formatCivilDate(date: CivilDate): string {
  return `${date.year}/${padTwo(date.month)}/${padTwo(date.day)}`
}

/**
 * 電力量 (kWh) を整数カンマ区切り形式でフォーマット。
 * TC-D-04.1: 例 1234567 → "1,234,567"
 * TC-E-04.1: undefined → "—"
 */
export function formatEnergyKwh(value: string | undefined): string {
  if (value === undefined || value === '') return FALLBACK
  const num = parseFloat(value)
  if (isNaN(num)) return FALLBACK
  return Math.round(num).toLocaleString('en-US')
}

/**
 * 契約単価 (円/kWh) を小数 2 桁 (四捨五入) でフォーマット。
 * TC-D-05.1: 例 "12.5" → "12.50", "12.345" → "12.35"
 * TC-E-04.1: undefined → "—"
 */
export function formatUnitPrice(value: string | undefined): string {
  if (value === undefined || value === '') return FALLBACK
  const num = parseFloat(value)
  if (isNaN(num)) return FALLBACK
  return num.toFixed(2)
}

/**
 * 数量 (MW) を入力精度のまま表示。
 * TC-D-06.1: 末尾ゼロを取り除く ("10.0" → "10", "10.5" → "10.5")
 * TC-E-04.1: undefined → "—"
 */
export function formatQuantityMw(value: string | undefined): string {
  if (value === undefined || value === '') return FALLBACK
  const num = parseFloat(value)
  if (isNaN(num)) return FALLBACK
  // parseFloat → toString で末尾ゼロを取り除く
  return String(num)
}

/**
 * 供給時間 (h) を末尾ゼロ除去で表示。
 * TC-D-07.1: 例 "744.0" → "744"
 */
export function formatSupplyHours(value: string | undefined): string {
  if (value === undefined || value === '') return FALLBACK
  const num = parseFloat(value)
  if (isNaN(num)) return FALLBACK
  return String(num)
}

/**
 * 実績利用率 (0-1 スケール) をパーセント形式でフォーマット。
 * TC-D-08.1: 例 "0.85" → "85.0%"
 * TC-E-04.1: undefined → "—"
 */
export function formatUtilizationPercent(value: string | undefined): string {
  if (value === undefined || value === '') return FALLBACK
  const num = parseFloat(value)
  if (isNaN(num)) return FALLBACK
  return `${(num * 100).toFixed(1)}%`
}

/**
 * 約定日を「yyyy/MM/dd」形式でフォーマット。
 * TC-D-09.1: 例 { year: 2025, month: 3, day: 20 } → "2025/03/20"
 */
export function formatExecutionDate(date: CivilDate): string {
  return formatCivilDate(date)
}

/**
 * 受渡期間を「yyyy/MM/dd〜yyyy/MM/dd」形式でフォーマット。
 * TC-D-10.1: 年またぎにも対応。
 */
export function formatDeliveryPeriod(from: CivilDate, to: CivilDate): string {
  return `${formatCivilDate(from)}〜${formatCivilDate(to)}`
}

/**
 * 電力量料金 (円) を整数カンマ区切り形式でフォーマット。
 * TC-D-03.1 の recap-total 行で使用。
 * TC-E-04.1: undefined → "—"
 */
export function formatTotalAmount(value: string | undefined): string {
  return formatEnergyKwh(value)
}

/**
 * 相対先名称を表示用にフォーマット。
 * TC-E-05.1: 空文字 → "—"。値があれば一覧・手入力取引閲覧モーダルと同じ規則で
 * 取引先コードを括弧併記する (コード未登録なら名称のみ。issue #7045)。
 */
export function formatCounterparty(
  name: string | undefined,
  code: string,
): string {
  if (name === undefined || name === '') return FALLBACK
  return formatPartnerWithCode(name, code)
}
